
// Preview adapter — Stampante "fittizia" che renderizza l'etichetta come PNG
// in una nuova scheda del browser. Usata per QA e demo senza hardware fisico.
//
// Usa lo stesso raster della stampa termica e dell’editor.

import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { renderLabelCanvas } from './labelRaster.js';

// Identità unica della stampante anteprima virtuale. Single source of truth:
// usata sia dall'adapter (discover) sia dalla LabelsPage che la inietta sempre
// nella lista stampanti come predefinita (nessuna configurazione richiesta).
export const PREVIEW_PRINTER_ID = 'preview-virtual';
export const PREVIEW_PRINTER_NAME = 'Anteprima a schermo';

/**
 * Evento "trackeat:preview" emesso quando la stampante anteprima virtuale
 * processa un job. Listener primario: la LabelsPage che apre un IonModal
 * in-app con il PNG renderizzato. Fallback: se nessun listener cattura
 * l'evento entro 100ms, apriamo la finestra esterna (uso desktop/web).
 */
export interface PreviewEventDetail {
  png: string;
  copies: number;
  template: LabelTemplate;
  /** Marker che il listener in-app può settare per dire "ho preso io". */
  handled?: boolean;
}

function dispatchInAppPreview(template: LabelTemplate, png: string, copies: number): boolean {
  try {
    const detail: PreviewEventDetail = { png, copies, template };
    window.dispatchEvent(new CustomEvent('trackeat:preview', { detail }));
    return detail.handled === true;
  } catch {
    return false;
  }
}

function openPreviewWindow(
  template: LabelTemplate,
  pngDataUrl: string,
  copies: number,
): void {
  const w = window.open('', '_blank');
  if (!w) {
    const a = document.createElement('a');
    a.href = pngDataUrl;
    a.download = `trackeat-etichetta-${Date.now()}.png`;
    a.click();
    return;
  }
  // Rapporto aspetto etichetta per scalare l'immagine a schermo intero preservando le proporzioni
  const aspect = template.height_mm / template.width_mm;
  const html = `<!doctype html><html lang="it"><head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"/>
  <title>Anteprima etichetta</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    html{height:100%}
    body{
      min-height:100%;
      background:#111827;
      color:#f9fafb;
      font-family:-apple-system,Segoe UI,Roboto,sans-serif;
      display:flex;
      flex-direction:column;
      align-items:center;
      justify-content:center;
      padding:24px 16px;
      gap:20px;
    }
    /* Fallback per Android Chrome dove vh non si calcola correttamente su about:blank */
    @supports (height: 100dvh){
      body{min-height:100dvh}
    }
    h1{font-size:15px;font-weight:600;text-align:center}
    .meta{font-size:11px;color:#9ca3af;text-align:center}
    .label-wrap{
      background:#fff;
      border-radius:10px;
      padding:20px;
      box-shadow:0 8px 32px rgba(0,0,0,.5);
      width:min(calc(100vw - 32px), 480px);
      max-width:480px;
    }
    .label-wrap img{display:block;width:100%;height:auto;border-radius:2px}
    .actions{display:flex;flex-wrap:wrap;gap:10px;justify-content:center;width:100%;max-width:480px}
    button{flex:1;min-width:120px;background:#2563eb;color:#fff;border:0;padding:14px 12px;border-radius:10px;font-size:15px;font-weight:600;cursor:pointer;-webkit-tap-highlight-color:transparent}
    button.alt{background:#374151}
    @media print{body{background:#fff;padding:0;justify-content:flex-start}h1,.meta,.actions{display:none}.label-wrap{box-shadow:none;padding:0;width:100%}}
  </style></head>
  <body>
  <h1>Anteprima etichetta</h1>
  <div class="meta">${escapeHtml(template.name)} &middot; ${template.width_mm}&times;${template.height_mm}&nbsp;mm &middot; ${copies}&times; copi${copies > 1 ? 'e' : 'a'}</div>
  <div class="label-wrap"><img src="${pngDataUrl}" alt="Etichetta"/></div>
  <div class="actions">
    <button onclick="window.print()">Stampa su carta</button>
    <button class="alt" onclick="(function(){var a=document.createElement('a');a.href='${pngDataUrl}';a.download='niveo-etichetta.png';a.click();})()">Scarica PNG</button>
    <button class="alt" onclick="window.close()">Chiudi</button>
  </div>
  </body></html>`;
  w.document.write(html);
  w.document.close();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

export class PreviewAdapter implements PrinterAdapter {
  readonly driver = 'preview' as const;

  async discover(): Promise<PrinterDevice[]> {
    return [{
      id: PREVIEW_PRINTER_ID,
      name: PREVIEW_PRINTER_NAME,
      driver: 'preview',
      connection: 'usb',
      address: 'preview://canvas',
    }];
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    return { deviceId: device.id, driver: this.driver };
  }

  async disconnect(_session: PrinterSession): Promise<void> {
    // no-op
  }

  async printLabel(
    _session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    const png = renderLabelCanvas(template, data).toDataURL('image/png');
    // Prima tenta il listener in-app (LabelsPage). Se nessuno marca handled,
    // apre la finestra esterna come fallback (desktop, dev, ecc).
    const handledInApp = dispatchInAppPreview(template, png, copies);
    if (!handledInApp) {
      openPreviewWindow(template, png, copies);
    }
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    return {
      online: true,
      paper_ok: true,
      cover_closed: true,
      battery_pct: null,
      error_code: null,
    };
  }
}
